import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export function useDevices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [controlLoading, setControlLoading] = useState(false);

  useEffect(() => {
    console.log('[useDevices] 🚀 Hook initialized');
    fetchDevices();

    // Subscribe to real-time changes
    const subscription = supabase
      .channel('devices_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'devices' },
        (payload) => {
          console.log('[useDevices] 📡 Device changed:', payload);
          fetchDevices();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchDevices = async () => {
    try {
      console.log('[useDevices] 🔄 Fetching devices from Supabase...');
      setLoading(true);
      
      const { data, error } = await supabase
        .from('devices')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('[useDevices] 📊 Query result:', {
        success: !error,
        dataLength: data?.length || 0,
        error: error?.message || null
      });

      if (error) throw error;

      if (!data || data.length === 0) {
        console.warn('[useDevices] ⚠️ No devices returned from database');
        setDevices([]);
        setError(null);
        setLoading(false);
        return;
      }

      console.log('[useDevices] 🔧 Transforming', data.length, 'devices...');

      // Transform data to match existing structure
      const transformedDevices = data.map(device => ({
        id: device.device_id,
        name: device.name,
        location: device.location,
        isLocked: device.is_locked,
        isOnline: device.status === 'online',
        batteryLevel: device.battery_level,
        lastSeen: new Date(device.last_seen),
        firmwareVersion: device.firmware_version,
        status: device.status,
        device_id: device.device_id,
        _id: device.id,
        _raw: device
      }));

      console.log('[useDevices] ✅ Transformed devices:', transformedDevices);

      setDevices(transformedDevices);
      setError(null);
    } catch (err) {
      console.error('[useDevices] ❌ Error fetching devices:', err);
      setError(err.message);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const lockDevice = async (deviceId) => {
    try {
      setControlLoading(true);
      console.log('[Lock] 🔒 Sending lock command to:', deviceId);

      const { data, error: updateError } = await supabase
        .from('devices')
        .update({
          pending_command: {
            action: 'lock',
            timestamp: new Date().toISOString(),
            source: 'web_app'
          },
          is_locked: true
        })
        .eq('device_id', deviceId)
        .select();

      if (updateError) throw updateError;

      console.log('[Lock] ✅ Command sent successfully:', data);

      setDevices(prev => prev.map(d => 
        d.device_id === deviceId 
          ? { ...d, isLocked: true, lastSeen: new Date() } 
          : d
      ));

      setTimeout(() => {
        fetchDevices();
      }, 2000);

      return { success: true, data };

    } catch (err) {
      console.error('[Lock] ❌ Error:', err);
      return { success: false, error: err.message };
    } finally {
      setControlLoading(false);
    }
  };

  const unlockDevice = async (deviceId) => {
    try {
      setControlLoading(true);
      console.log('[Unlock] 🔓 Sending unlock command to:', deviceId);

      const { data, error: updateError } = await supabase
        .from('devices')
        .update({
          pending_command: {
            action: 'unlock',
            timestamp: new Date().toISOString(),
            source: 'web_app'
          },
          is_locked: false
        })
        .eq('device_id', deviceId)
        .select();

      if (updateError) throw updateError;

      console.log('[Unlock] ✅ Command sent successfully:', data);

      setDevices(prev => prev.map(d => 
        d.device_id === deviceId 
          ? { ...d, isLocked: false, lastSeen: new Date() } 
          : d
      ));

      setTimeout(() => {
        fetchDevices();
        console.log('[Unlock] 🔄 Refetch #1: Command processed');
      }, 2000);

      setTimeout(() => {
        fetchDevices();
        console.log('[Unlock] 🔄 Refetch #2: After auto-lock period');
      }, 7000);

      return { success: true, data };

    } catch (err) {
      console.error('[Unlock] ❌ Error:', err);
      return { success: false, error: err.message };
    } finally {
      setControlLoading(false);
    }
  };

  const updateDeviceLock = async (deviceId, isLocked) => {
    if (isLocked) {
      return await lockDevice(deviceId);
    } else {
      return await unlockDevice(deviceId);
    }
  };

  return { 
    devices, 
    loading, 
    error, 
    controlLoading,
    refetch: fetchDevices,
    updateDeviceLock,
    lockDevice,
    unlockDevice
  };
}